import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Calendar from '../../Calendar/Calendar';
import { CalendarProvider, useCalendar } from '../../../contexts/CalendarContext';
import { websocketService } from '../../../services/websocketService';
import "../../../styles/NewCalendar.css";
import ListViewProposed from './ListViewProposed';

/**
 * ProposedBookingsPage component using the custom Calendar component
 * This component displays proposed orders in a calendar view using the API
 */
const ProposedBookingsPageContent = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState('list');
  const [isKindMode, setIsKindMode] = useState(false);
  const { events, setEvents, currentDate, updateFilters } = useCalendar();

  // Helper function to get current week number
  const getCurrentWeek = (date = new Date()) => {
    const now = new Date(date);
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    return Math.ceil(diff / oneWeek);
  };

  // Get initial week from calendar's current date or current date
  const getInitialWeek = () => {
    if (currentDate) {
      return getCurrentWeek(currentDate);
    }
    return getCurrentWeek();
  };

  // Initial filters state with "optioned" and "hold" preselected to match the original component
  const initialFilters = {
    bookingStatus: 'optioned,hold'
  };

  // Initialize WebSocket connection
  useEffect(() => {
    websocketService.connect();

    // Subscribe to booking updates
    const handleBookingUpdate = (data) => {
      // console.log('Received booking update:', data);
      setEvents(prevEvents => {
        // Find and update the existing event if it exists
        const eventIndex = prevEvents.findIndex(event => event.id === data.id);
        if (eventIndex !== -1) {
          const updatedEvents = [...prevEvents];
          updatedEvents[eventIndex] = { ...updatedEvents[eventIndex], ...data };
          return updatedEvents;
        }
        // Add new event if it doesn't exist
        return [...prevEvents, data];
      });
    };

    websocketService.subscribe('booking', handleBookingUpdate);

    // Cleanup on unmount
    return () => {
      websocketService.unsubscribe('booking', handleBookingUpdate);
      websocketService.disconnect();
    };
  }, [setEvents]);

  // Effect to handle KIND mode toggle for calendar view
  useEffect(() => {
    if (view === 'calendar' && updateFilters) {
      if (isKindMode) {
        // When KIND mode is ON, set filter to "digital Production"
        updateFilters({ set: 'digital Production' });
      } else {
        // When KIND mode is OFF, clear the set filter
        updateFilters({ set: null });
      }
    }
  }, [isKindMode, view, updateFilters]);

  // Handle event click to navigate to the booking details
  const handleEventClick = (event) => {
    if (event && event.id) {
      navigate(`/admin/detail?id=${event.id}`);
    }
  };

  // Switch between calendar and list views
  const switchView = (viewType) => {
    setView(viewType);
  };

  // Separate optioned and hold events for ListViewProposed
  const optionedEvents = events?.filter(event => event.status === "optioned") || [];
  const holdEvents = events?.filter(event => event.status === "hold") || [];

  return (
    <div className="content-wrapper page">
      <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '20px'}}>
        <h1>Proposed Orders</h1>
        <div>
          <button 
            className={view === 'calendar' ? 'big-button' : 'big-button-active'} 
            onClick={() => switchView('list')}>
            List View
          </button>
          <button 
            className={view === 'calendar' ? 'big-button-active' : 'big-button'} 
            onClick={() => switchView('calendar')}>
            Calendar View
          </button>
        </div>
      </div>
      
      <div style={{ height: "100%", marginTop: '20px', width: '100%'}}>
        {isLoading && (
          <div style={{textAlign: 'center', padding: '20px'}}>
            Loading calendar...
          </div>
        )}
        
        {view === 'calendar' ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '20px', paddingRight: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontWeight: '700', fontSize: '14px', textTransform: 'uppercase' }}>KIND</span>
                <label style={{ 
                  position: 'relative', 
                  display: 'inline-block', 
                  width: '60px', 
                  height: '34px',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={isKindMode}
                    onChange={() => setIsKindMode(!isKindMode)}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: isKindMode ? '#34C759' : '#ccc',
                    transition: '0.4s',
                    borderRadius: '34px',
                    cursor: 'pointer'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '""',
                      height: '26px',
                      width: '26px',
                      left: isKindMode ? '30px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }} />
                  </span>
                </label>
              </div>
            </div>
            <Calendar
              onEventClick={handleEventClick}
              initialFilters={initialFilters}
            />
          </>
        ) : (
          <ListViewProposed 
            events={optionedEvents} 
            holdevents={holdEvents} 
            authToken={currentUser?.token}
            initialWeek={getInitialWeek()}
          />
        )}
      </div>
    </div>
  );
};

// Wrap with CalendarProvider, seeding the proposed-order status filter so the
// VERY FIRST fetch already loads all optioned/hold orders (no date-range limit),
// for both the list and calendar views.
const ProposedBookingsPage = () => (
  <CalendarProvider initialFilters={{ bookingStatus: 'optioned,hold' }}>
    <ProposedBookingsPageContent />
  </CalendarProvider>
);

export default ProposedBookingsPage;
